import { create } from 'zustand';
import { getLocalDateString } from '@/stores/tasks';
import { ApiError } from '@/api/client';
import {
  fetchAdministratieDay,
  setCheckinWork,
  setGemeld,
  setIngecheckt,
  setAanwezig,
  type AdministratieRow,
  type WorkerRow,
} from '@/api/administratie';

const limosaOpen = (l: { badge: string } | null | undefined) => l?.badge === 'rood' || l?.badge === 'te_controleren';

/** Worst first — the same order the API uses (utils/limosa-status.ts). */
const LIMOSA_SEVERITY = ['rood', 'te_controleren', 'oranje', 'groen'];

/**
 * The company badge from its workers' badges, as the API computes it (decision
 * 15): all workers until presence is reported, then only the present ones; the
 * worst wins, and between two oranje the one that ends first. "Limosa verplicht"
 * is per person (decision 17): a worker whose badge is "niet nodig" is not
 * judged, and when nobody on the roster needs one the company is "niet nodig".
 * Used after a presence change so the badge and the open state never lag behind
 * it. Returns the row's badge unchanged when a worker's badge is unknown.
 */
export function companyLimosa(row: AdministratieRow): AdministratieRow['limosa'] {
  if (!row.workers || row.workers.some((w) => w.limosa == null)) return row.limosa;
  const roster = (row.presenceReported ? row.workers.filter((w) => w.aanwezig === true) : row.workers)
    .filter((w) => w.limosa!.badge !== 'niet_nodig');
  if (roster.length === 0) return { badge: 'niet_nodig', geldigTot: null };
  let worst: AdministratieRow['limosa'] = null;
  for (const w of roster) {
    const l = w.limosa!;
    if (
      worst === null
      || LIMOSA_SEVERITY.indexOf(l.badge) < LIMOSA_SEVERITY.indexOf(worst.badge)
      || (l.badge === worst.badge && l.geldigTot !== null && worst.geldigTot !== null && l.geldigTot < worst.geldigTot)
    ) worst = l;
  }
  return worst ?? row.limosa;
}

/**
 * A row needs action when something is missing.
 * - One person or a team: meldingsmail off, not ingecheckt, or Limosa rood / te controleren.
 * - A subcontractor with workers: meldingsmail off; on a day that has come, nobody
 *   reported present; a present worker not ingecheckt; or the Limosa (present
 *   workers, or all of them until presence is reported) rood / te controleren.
 *   An old migrated check-in on the company itself counts as checked in.
 */
export function isOpenRow(row: AdministratieRow, today: string = getLocalDateString()): boolean {
  if (row.installerId == null) return false;
  if (row.gemeld === false || limosaOpen(row.limosa)) return true;
  if (!row.workers) return row.ingecheckt === false;

  const legacyCheckin = row.ingecheckt === true;
  if (!row.presenceReported) return !legacyCheckin && row.taskDate <= today;
  return !legacyCheckin && row.workers.some((w) => w.aanwezig === true && w.ingecheckt === false);
}

function shiftDate(date: string, days: number): string {
  const d = new Date(date + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return getLocalDateString(d);
}

function message(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

interface AdministratieState {
  selectedDate: string;
  rows: AdministratieRow[];
  truncated: boolean;
  loading: boolean;
  error: string | null;
  /** A failed toggle, shown above the list; the row itself is rolled back. */
  saveError: string | null;
  searchTerm: string;
  onlyOpen: boolean;
  busyKeys: Set<string>;
  fetchDay: () => Promise<void>;
  nextDay: () => void;
  prevDay: () => void;
  goToToday: () => void;
  setSearchTerm: (term: string) => void;
  setOnlyOpen: (value: boolean) => void;
  clearSaveError: () => void;
  toggleGemeld: (row: AdministratieRow, value: boolean) => Promise<void>;
  toggleIngecheckt: (row: AdministratieRow, value: boolean) => Promise<void>;
  /** Set (or clear with '') the werf's Checkin@work number on every row of the project. */
  saveCheckinWork: (projectId: number, value: string) => Promise<void>;
  /** A worker of the row's installer present / absent on that task. */
  toggleAanwezig: (row: AdministratieRow, workerId: number, value: boolean) => Promise<void>;
  /** A present worker of the row's installer checked in / out. */
  toggleWorkerIngecheckt: (row: AdministratieRow, workerId: number, value: boolean) => Promise<void>;
}

export const useAdministratieStore = create<AdministratieState>((set, get) => {
  /**
   * Optimistic update of one field on every row `match` selects, then the write.
   * On failure only THAT field goes back to its old value: the other toggle on
   * the same row may have its own request in flight (or already saved), and it
   * keeps whatever value it has now.
   */
  async function optimistic(
    busyKey: string,
    match: (r: AdministratieRow) => boolean,
    field: 'gemeld' | 'ingecheckt' | 'checkinWorkNr',
    value: boolean | string | null,
    writeFn: () => Promise<unknown>,
    failMessage: string,
  ) {
    const before = new Map(get().rows.filter(match).map((r) => [r.key, r[field]]));
    set((s) => ({
      saveError: null,
      busyKeys: new Set(s.busyKeys).add(busyKey),
      rows: s.rows.map((r) => (match(r) ? { ...r, [field]: value } : r)),
    }));
    try {
      await writeFn();
    } catch (err) {
      set((s) => ({
        saveError: message(err, failMessage),
        rows: s.rows.map((r) => (before.has(r.key) ? { ...r, [field]: before.get(r.key)! } : r)),
      }));
    } finally {
      set((s) => {
        const busyKeys = new Set(s.busyKeys);
        busyKeys.delete(busyKey);
        return { busyKeys };
      });
    }
  }

  /**
   * The same for one field of one worker on one row: on failure only that
   * worker's field returns, whatever else changed meanwhile.
   */
  /**
   * One field of one worker on one task. Presence and check-in are stored on the
   * TASK, so the same worker shows on every row of that task whose installer
   * picks from the same company (two teams of one company on one task): every
   * such row changes together, each recomputes its own company badge, and a
   * failure puts each row's own previous value back. The busy key is
   * task+worker+field, so the second row cannot start a competing write.
   */
  async function optimisticWorker(
    taskId: number,
    workerId: number,
    field: 'aanwezig' | 'ingecheckt',
    value: boolean,
    writeFn: () => Promise<unknown>,
    failMessage: string,
  ) {
    const busyKey = `${field}-${taskId}-${workerId}`;
    if (get().busyKeys.has(busyKey)) return;
    const shows = (r: AdministratieRow) => r.taskId === taskId && !!r.workers?.some((w) => w.id === workerId);
    const before = new Map(get().rows.filter(shows).map((r) => [r.key, r.workers!.find((w) => w.id === workerId)![field]]));
    const setWorker = (rows: AdministratieRow[], valueFor: (rowKey: string) => boolean | null) => rows.map((r) => (before.has(r.key) && r.workers
      ? { ...r, workers: r.workers.map((w): WorkerRow => (w.id === workerId ? { ...w, [field]: valueFor(r.key) } : w)) }
      : r));
    // Presence decides who the company badge judges, so recompute it with it.
    const recompute = (rows: AdministratieRow[]) => rows.map((r) => {
      if (!before.has(r.key) || !r.workers) return r;
      const next = { ...r, presenceReported: r.workers.some((w) => w.aanwezig === true) };
      return { ...next, limosa: companyLimosa(next) };
    });
    set((s) => ({ saveError: null, busyKeys: new Set(s.busyKeys).add(busyKey), rows: recompute(setWorker(s.rows, () => value)) }));
    try {
      await writeFn();
    } catch (err) {
      set((s) => ({ saveError: message(err, failMessage), rows: recompute(setWorker(s.rows, (k) => before.get(k) ?? null)) }));
    } finally {
      set((s) => {
        const busyKeys = new Set(s.busyKeys);
        busyKeys.delete(busyKey);
        return { busyKeys };
      });
    }
  }

  return {
    selectedDate: getLocalDateString(),
    rows: [],
    truncated: false,
    loading: false,
    error: null,
    saveError: null,
    searchTerm: '',
    onlyOpen: false,
    busyKeys: new Set(),

    fetchDay: async () => {
      const date = get().selectedDate;
      set({ loading: true, error: null });
      try {
        const data = await fetchAdministratieDay(date);
        // A slower answer for a day we already left is dropped.
        if (get().selectedDate !== date) return;
        set({ rows: data.rows, truncated: data.truncated, loading: false });
      } catch (err) {
        if (get().selectedDate !== date) return;
        set({ loading: false, error: message(err, 'Taken ophalen mislukt') });
      }
    },

    nextDay: () => set({ selectedDate: shiftDate(get().selectedDate, 1), saveError: null }),
    prevDay: () => set({ selectedDate: shiftDate(get().selectedDate, -1), saveError: null }),
    goToToday: () => set({ selectedDate: getLocalDateString(), saveError: null }),
    setSearchTerm: (searchTerm) => set({ searchTerm }),
    setOnlyOpen: (onlyOpen) => set({ onlyOpen }),
    clearSaveError: () => set({ saveError: null }),

    // Gemeld holds for the whole project: every row of this installer on this project flips.
    toggleGemeld: (row, value) => {
      if (row.projectId == null || row.installerId == null) return Promise.resolve();
      const { projectId, installerId } = row;
      return optimistic(
        `gemeld-${projectId}-${installerId}`,
        (r) => r.projectId === projectId && r.installerId === installerId,
        'gemeld',
        value,
        () => setGemeld(projectId, installerId, value),
        'Meldingsmail bijwerken mislukt',
      );
    },

    saveCheckinWork: (projectId, value) => {
      const trimmed = value.trim();
      return optimistic(
        `checkinwork-${projectId}`,
        (r) => r.projectId === projectId,
        'checkinWorkNr',
        trimmed === '' ? null : trimmed,
        () => setCheckinWork(projectId, trimmed),
        'Checkin@work bijwerken mislukt',
      );
    },

    toggleAanwezig: (row, workerId, value) => {
      if (row.installerId == null) return Promise.resolve();
      const installerId = row.installerId;
      return optimisticWorker(row.taskId, workerId, 'aanwezig', value,
        () => setAanwezig(row.taskId, installerId, workerId, value), 'Aanwezigheid bijwerken mislukt');
    },

    toggleWorkerIngecheckt: (row, workerId, value) => {
      if (row.installerId == null) return Promise.resolve();
      const installerId = row.installerId;
      return optimisticWorker(row.taskId, workerId, 'ingecheckt', value,
        () => setIngecheckt(row.taskId, installerId, value, workerId), 'Check-in bijwerken mislukt');
    },

    // Check-in is per task: only this row.
    toggleIngecheckt: (row, value) => {
      if (row.installerId == null) return Promise.resolve();
      const { taskId, installerId, key } = row;
      return optimistic(
        `ingecheckt-${key}`,
        (r) => r.key === key,
        'ingecheckt',
        value,
        () => setIngecheckt(taskId, installerId, value),
        'Check-in bijwerken mislukt',
      );
    },
  };
});
